// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — shared single-in-flight latch (ABB-S4, INV-INFLIGHT)
// ═══════════════════════════════════════════════════════════════════════════
//
// One PROCESS-WIDE latch guarding "a grouped-undo AI turn is open right now",
// shared across ALL in-page write clients so a second turn can never NEST into an
// open `begin-batch` (`editorStore.ts:3367` increments `depth + 1` when a batch is
// already open) and corrupt the one-undo-per-turn contract:
//
//   • the bridge `command.run` handler (ABB-S4),
//   • the window seam `editorRunAiCommandBatch` / `editorRunAiCommand`
//     (`editorStore.ts`),
//   • the `AiDraftingPanel` Accept (`AiDraftingPanel.tsx`).
//
// The companion's `command-in-flight` 409 only covers bridge-vs-BRIDGE (one HTTP
// command per session at the broker). This latch covers bridge-vs-LOCAL: a panel
// Accept or a window-seam call running WHILE a bridge turn is mid-dispatch (or
// vice-versa) would otherwise fold into the open batch. The bridge maps a held
// latch to `command-in-flight` (the closest taxonomy code, see `rejectMapping`).
//
// WHY HERE (ПРИОРИТЕТ #2): a pure module-singleton that imports NOTHING. The
// editorStore-side check-sites (`editorStore.ts` window seam, `AiDraftingPanel`)
// IMPORT this leaf — `editorStore → agentBridge/inFlightLatch` is the ALLOWED
// one-directional edge (S7's mount already imports `agentBridge`); it is NOT a
// cycle because this module (and the whole `agentBridge/` graph) imports no
// editorStore. `npm run deps:graph` stays cycles=0.
//
// SCOPE — process-wide module state (one bridge + one panel + one window seam per
// tab). A single boolean + a holder tag is sufficient; the latch is NON-reentrant
// (a holder must `release` before any client — including itself — may `tryAcquire`
// again) so a leaked acquire is a visible bug, not a silent nest.
//
// LEAF module: imports NOTHING. No socket, no editorStore, no node-companion.
// ═══════════════════════════════════════════════════════════════════════════

/** Which in-page client currently holds the single-in-flight latch. */
export type InFlightHolder = "bridge" | "panel" | "window-seam";

let heldBy: InFlightHolder | null = null;

/**
 * Attempt to claim the single-in-flight latch for `holder`. Returns `true` and
 * marks the latch held when it was free; returns `false` (the latch is held by
 * ANY client, incl. this same holder — non-reentrant) without mutating state when
 * it is already taken. A `true` MUST be paired with exactly one {@link releaseInFlight}.
 */
export function tryAcquireInFlight(holder: InFlightHolder): boolean {
  if (heldBy !== null) {
    return false;
  }
  heldBy = holder;
  return true;
}

/**
 * Release the latch. Idempotent + holder-checked: releasing when free is a no-op,
 * and releasing on behalf of a holder that does NOT currently hold it is a no-op
 * (so a stale release from an aborted client cannot free a DIFFERENT client's
 * live turn). Pass the SAME holder that acquired it.
 */
export function releaseInFlight(holder: InFlightHolder): void {
  if (heldBy === holder) {
    heldBy = null;
  }
}

/** Whether ANY client currently holds the latch (read-only; for tests / guards). */
export function isInFlightHeld(): boolean {
  return heldBy !== null;
}

/** The current holder, or `null` if free (read-only; for tests / diagnostics). */
export function currentInFlightHolder(): InFlightHolder | null {
  return heldBy;
}

/**
 * TEST-ONLY: force-clear the latch (a test that exercises a throwing path may
 * leave it held). Production code never calls this — it releases via the
 * acquire/release pair. Exported so unit tests reset module state between cases.
 */
export function __resetInFlightLatchForTest(): void {
  heldBy = null;
}
